import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  ArrowUpDown,
  LayoutList,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon } from '../../icons';
import { Button } from '../../ui';
import { ProjectDto } from '../../../services';
import './TimelineView.css';

interface TimelineViewProps {
  pieces: ProjectDto[];
  isLoading?: boolean;
  onPieceClick?: (piece: ProjectDto) => void;
  orientation?: 'horizontal' | 'vertical';
}

type SortOption = 'created' | 'updated' | 'title';
type GroupOption = 'none' | 'discipline' | 'stage';

const getDisciplineIcon = (type: string): React.ReactNode => {
  switch (type.toLowerCase()) {
    case 'music':
      return <MusicIcon size={16} />;
    case 'art':
      return <VisualArtIcon size={16} />;
    case 'writing':
      return <WritingIcon size={16} />;
    case 'video':
      return <VideoIcon size={16} />;
    default:
      return <MusicIcon size={16} />;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const sortPieces = (pieces: ProjectDto[], sortBy: SortOption): ProjectDto[] => {
  return [...pieces].sort((a, b) => {
    switch (sortBy) {
      case 'updated':
        return new Date(b.updatedAt || b.createdAt).getTime() -
               new Date(a.updatedAt || a.createdAt).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      case 'created':
      default:
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
  });
};

const groupPieces = (
  pieces: ProjectDto[],
  groupBy: GroupOption
): { group: string; pieces: ProjectDto[] }[] => {
  if (groupBy === 'none') {
    return [{ group: '', pieces }];
  }

  const groups: Record<string, ProjectDto[]> = {};

  pieces.forEach((piece) => {
    const key = groupBy === 'discipline' ? piece.projectType : piece.currentStage;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(piece);
  });

  return Object.entries(groups)
    .map(([group, groupPieces]) => ({ group, pieces: groupPieces }))
    .sort((a, b) => a.group.localeCompare(b.group));
};

export const TimelineView: React.FC<TimelineViewProps> = ({
  pieces,
  isLoading = false,
  onPieceClick,
  orientation: defaultOrientation = 'horizontal',
}) => {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortOption>('created');
  const [groupBy, setGroupBy] = useState<GroupOption>('none');
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>(defaultOrientation);
  const [zoomLevel, setZoomLevel] = useState(1);

  const sortedPieces = useMemo(() => sortPieces(pieces, sortBy), [pieces, sortBy]);
  const groupedPieces = useMemo(
    () => groupPieces(sortedPieces, groupBy),
    [sortedPieces, groupBy]
  );

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 2));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));

  if (isLoading) {
    return (
      <div className="timeline-view timeline-view--loading">
        <div className="timeline-view__skeleton" />
      </div>
    );
  }

  if (pieces.length === 0) {
    return (
      <div className="timeline-view timeline-view--empty">
        <p className="timeline-view__empty-text">
          {t('opus.dashboard.timeline.empty', 'No pieces to display on timeline')}
        </p>
      </div>
    );
  }

  return (
    <div className={`timeline-view timeline-view--${orientation}`}>
      {/* Controls */}
      <div className="timeline-view__controls">
        <div className="timeline-view__control-group">
          <label className="timeline-view__label">
            <ArrowUpDown size={14} />
            {t('opus.dashboard.timeline.sortBy', 'Sort by')}
          </label>
          <select
            className="timeline-view__select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
          >
            <option value="created">{t('opus.dashboard.timeline.sortCreated', 'Created Date')}</option>
            <option value="updated">{t('opus.dashboard.timeline.sortUpdated', 'Last Updated')}</option>
            <option value="title">{t('opus.dashboard.timeline.sortTitle', 'Title')}</option>
          </select>
        </div>

        <div className="timeline-view__control-group">
          <label className="timeline-view__label">
            <LayoutList size={14} />
            {t('opus.dashboard.timeline.groupBy', 'Group by')}
          </label>
          <select
            className="timeline-view__select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupOption)}
          >
            <option value="none">{t('opus.dashboard.timeline.groupNone', 'No Grouping')}</option>
            <option value="discipline">{t('opus.dashboard.timeline.groupDiscipline', 'Discipline')}</option>
            <option value="stage">{t('opus.dashboard.timeline.groupStage', 'Stage')}</option>
          </select>
        </div>

        <div className="timeline-view__zoom-controls">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.5}
            aria-label={t('opus.dashboard.timeline.zoomOut', 'Zoom out')}
          >
            <ZoomOut size={16} />
          </Button>
          <span className="timeline-view__zoom-level">{Math.round(zoomLevel * 100)}%</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 2}
            aria-label={t('opus.dashboard.timeline.zoomIn', 'Zoom in')}
          >
            <ZoomIn size={16} />
          </Button>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOrientation(orientation === 'horizontal' ? 'vertical' : 'horizontal')}
          className="timeline-view__orientation-toggle"
        >
          {orientation === 'horizontal'
            ? t('opus.dashboard.timeline.vertical', 'Vertical')
            : t('opus.dashboard.timeline.horizontal', 'Horizontal')}
        </Button>
      </div>

      {/* Timeline Content */}
      <div
        className="timeline-view__content"
        style={{ '--timeline-zoom': zoomLevel } as React.CSSProperties}
      >
        {groupedPieces.map(({ group, pieces: groupPieces }) => (
          <div key={group || 'ungrouped'} className="timeline-view__group">
            {group && (
              <h3 className="timeline-view__group-title">
                {groupBy === 'discipline' && getDisciplineIcon(group)}
                {group}
              </h3>
            )}

            <div className="timeline-view__track">
              {/* Timeline line */}
              <div className="timeline-view__line" />

              {/* Piece nodes */}
              {groupPieces.map((piece, index) => (
                <button
                  key={piece.id}
                  className={`timeline-view__node ${
                    piece.status === 'Completed' ? 'timeline-view__node--completed' : ''
                  }`}
                  onClick={() => onPieceClick?.(piece)}
                  type="button"
                  style={
                    {
                      '--node-index': index,
                      '--total-nodes': groupPieces.length,
                    } as React.CSSProperties
                  }
                >
                  <div className="timeline-view__node-marker">
                    {piece.status === 'Completed' ? (
                      <CheckCircle size={18} />
                    ) : (
                      getDisciplineIcon(piece.projectType)
                    )}
                  </div>
                  <div className="timeline-view__node-content">
                    <span className="timeline-view__node-title">{piece.title}</span>
                    <span className="timeline-view__node-stage">{piece.currentStage}</span>
                    <span className="timeline-view__node-date">
                      {formatDate(piece.createdAt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineView;
